<template>
  <main class="terrain-campaign-form" aria-live="polite">
    <section class="terrain-campaign-form__content">
      <p v-if="accessStatus === 'loading'" class="terrain-campaign-form__status">Comprobando acceso a la campaña…</p>
      <section v-else-if="accessStatus === 'denied'" class="terrain-campaign-form__notice" role="alert">
        <h1>Campaña no disponible</h1>
        <p>No tiene acceso a esta campaña o ya no se encuentra vigente.</p>
      </section>
      <section v-else-if="accessStatus === 'error'" class="terrain-campaign-form__notice" role="alert">
        <h1>No fue posible cargar la campaña</h1>
        <p>Intente nuevamente. Si el problema persiste, contacte al administrador.</p>
        <button type="button" @click="loadAccess">Reintentar</button>
      </section>
      <section v-else-if="accessStatus === 'submitted'" class="terrain-campaign-form__notice">
        <h1>Respuesta enviada</h1>
        <p>Gracias, su respuesta fue registrada correctamente.</p>
      </section>
      <template v-else-if="accessStatus === 'ready'">
        <header class="terrain-campaign-form__header">
          <p class="terrain-campaign-form__eyebrow">Levantamiento en terreno</p>
          <h1>{{ definition.title || 'Campaña de terreno' }}</h1>
          <p v-if="definition.description">{{ definition.description }}</p>
        </header>

        <!--
          GpsCapture se mantiene montado pero oculto: captura GPS_Original en
          segundo plano apenas se abre el formulario (para que su marca de
          tiempo refleje el inicio del levantamiento), pero la interacción
          visible con la ubicación (validar/reintentar) se posterga al modal
          final, justo antes de enviar.
        -->
        <GpsCapture
          ref="gpsCapture"
          class="terrain-campaign-form__gps-hidden"
          @captured="onGpsCaptured"
          @capture-error="gpsError = $event.message"
          @submission-blocked="gpsBlockedMessage = $event.message"
        />

        <!--
          Precarga silenciosa: apenas hay una posición conocida, se monta un
          mapa desechable fuera de pantalla (dimensiones reales, no
          display:none, para que OpenLayers sí calcule y pida los teselas)
          de modo que cuando el usuario llegue al modal de envío, la zona ya
          esté cacheada por el navegador y el mapa real cargue al instante.
        -->
        <div v-if="gpsOriginal" class="terrain-campaign-form__map-preload" aria-hidden="true">
          <LocationPickerMap :coordinates="gpsOriginal.captured.coordinates" />
        </div>

        <TerrainFormRenderer
          :definition="definition"
          :answers="answers"
          mode="respond"
          :campaign-id="campaignId"
          :template-version-id="content.templateVersionId"
		  :readonly-question-ids="lockedQuestionIds"
          @update:answers="onAnswersChanged"
        />

        <footer class="terrain-campaign-form__footer">
          <AutosaveController
            :api="terrainApi"
            :campaign-id="campaignId"
            :template-version-id="content.templateVersionId"
            :answers="answers"
            :gps-original="gpsOriginal"
            :gps-final="gpsFinal"
            :client-revision="draftClientRevision"
            :resource-version="draftResourceVersion"
            :enabled="autosaveReady"
			:prefill-case-id="prefillCaseId"
            @saved="applySavedDraft"
          />
          <button type="button" class="terrain-campaign-form__submit" @click="openSubmitModal">Enviar formulario</button>
        </footer>

        <modal name="terrain-submission-confirm" :width="480" height="auto" :adaptive="true" :scrollable="true">
          <div class="terrain-submission-modal">
            <h2>Confirmar ubicación y enviar</h2>
            <p class="terrain-submission-modal__hint">
              Verifique que el marcador esté en el lugar correcto. Toque el mapa para ajustarlo si es necesario.
            </p>

            <LocationPickerMap v-if="pickedCoordinates" :coordinates="pickedCoordinates" @update:coordinates="onLocationPicked" />
            <p v-else-if="capturingFinal" class="terrain-submission-modal__status">Obteniendo ubicación…</p>

            <div v-if="gpsFinal" class="terrain-submission-modal__accuracy">
              <span>Precisión GPS: {{ accuracyLabel }}</span>
              <button
                type="button"
                class="terrain-submission-modal__gps-btn"
                aria-label="Actualizar con GPS"
                title="Actualizar con GPS"
                :disabled="capturingFinal"
                @click="retryFinal"
              >
                <font-awesome-icon icon="location-arrow" />
              </button>
            </div>

            <p v-if="gpsError" class="terrain-submission-modal__error" role="alert">{{ gpsError }}</p>
            <p v-if="gpsBlockedMessage" class="terrain-submission-modal__error" role="alert">
              {{ gpsBlockedMessage }}
              <button type="button" class="terrain-submission-modal__retry" @click="retryFinal">Reintentar</button>
            </p>
            <p v-if="submitError" class="terrain-submission-modal__error" role="alert">{{ submitError }}</p>

            <div class="terrain-submission-modal__actions">
              <button type="button" class="terrain-submission-modal__cancel" :disabled="submitting" @click="closeSubmitModal">
                Cancelar
              </button>
              <button type="button" class="terrain-submission-modal__confirm" :disabled="!canConfirmSubmit" @click="confirmSubmit">
                {{ submitting ? 'Enviando…' : 'Confirmar y enviar' }}
              </button>
            </div>
          </div>
        </modal>
      </template>
    </section>
  </main>
</template>

<script>
import TerrainFormRenderer from "../../components/terrain/form/TerrainFormRenderer.vue";
import GpsCapture from "../../components/terrain/form/GpsCapture.vue";
import LocationPickerMap from "../../components/terrain/form/LocationPickerMap.vue";
import { classifyGpsAccuracy } from "../../components/terrain/form/LocationField.vue";
import AutosaveController from "../../components/terrain/form/AutosaveController.vue";
import { createTerrainApi } from "../../services/terrain/api.js";

const ACCURACY_LABELS = { buena: "Buena", aceptable: "Aceptable", advertida: "Muy poca precisión, favor ajustar" };

const SUBMIT_ERROR_MESSAGES = {
  TERRAIN_FORBIDDEN: "No tiene autorización para enviar esta respuesta.",
  TERRAIN_CAMPAIGN_CLOSED: "La campaña no acepta envíos en este momento.",
  TERRAIN_RESPONSE_LIMIT_REACHED: "Ya alcanzó el máximo de respuestas permitidas para esta campaña.",
  TERRAIN_IDEMPOTENCY_KEY_REUSED: "Este envío ya fue registrado.",
  TERRAIN_RESOURCE_NOT_AVAILABLE: "La campaña ya no está disponible.",
};

export default {
  name: "TerrainCampaignForm",
  components: { TerrainFormRenderer, GpsCapture, LocationPickerMap, AutosaveController },
  data() {
    return {
      accessStatus: "loading",
      content: null,
      answers: [],
      gpsOriginal: null,
      gpsFinal: null,
      gpsError: "",
      gpsBlockedMessage: "",
      capturingFinal: false,
      submitting: false,
      submitError: "",
      draftClientRevision: 0,
      draftResourceVersion: 0,
      // Se activa recién cuando el respondente edita una respuesta de
      // verdad (ver AutosaveController.vue) — no con la restauración del
      // borrador ni con la captura silenciosa de GPS_Original al abrir.
      autosaveReady: false,
	  selectedPrefillCase: null,
    };
  },
  computed: {
    campaignId() { return ((this.$route || {}).params || {}).campaignId; },
	prefillCaseId() { return (((this.$route || {}).query || {}).prefillCaseId) || ''; },
    definition() { return (this.content && this.content.definition) || { schemaVersion: 1, sections: [], reviewSections: [] }; },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
	lockedQuestionIds() {
	  if (!this.selectedPrefillCase || !this.content || !this.content.prefill) return [];
	  const populated = new Set((this.selectedPrefillCase.answers || []).map((answer) => answer.questionId));
	  return (this.content.prefill.fields || []).filter((field) => field.editable === false && populated.has(field.questionId)).map((field) => field.questionId);
	},
    canConfirmSubmit() { return Boolean(this.gpsFinal) && !this.submitting && !this.capturingFinal; },
    // pickedCoordinates prioriza la corrección manual (si el usuario tocó el
    // mapa) sobre la captura GPS cruda, siempre en [lon, lat].
    pickedCoordinates() {
      if (!this.gpsFinal) return null;
      return (this.gpsFinal.corrected && this.gpsFinal.corrected.coordinates) || this.gpsFinal.captured.coordinates;
    },
    accuracyLabel() {
      if (!this.gpsFinal) return "";
      if (this.gpsFinal.captured.manual) return "Manual";
      const meters = this.gpsFinal.captured.accuracyMeters;
      return `${meters} m (${ACCURACY_LABELS[classifyGpsAccuracy(meters)]})`;
    },
  },
  watch: {
    campaignId() { this.loadAccess(); },
	prefillCaseId() { this.loadAccess(); },
  },
  created() { this.loadAccess(); },
  methods: {
    async loadAccess() {
      this.accessStatus = "loading";
      this.content = null;
      this.answers = [];
	  this.selectedPrefillCase = null;
      try {
        const response = await this.$http.get(`${this.$store.getters.backendurl}api/v1/terrain/campaigns/${this.campaignId}/access`);
        if (!response.data || !response.data.access || !response.data.content) {
          this.accessStatus = "denied";
          return;
        }
        this.content = response.data.content;
		if (this.content.prefill && this.content.prefill.enabled) {
		  if (!this.prefillCaseId) { this.$router.replace({ name: 'terrain-prefill-inbox', params: { campaignId: this.campaignId } }); return; }
		  const inbox = await this.terrainApi.listPrefillInbox(this.campaignId);
		  this.selectedPrefillCase = (inbox.data.items || []).find((item) => item.id === this.prefillCaseId) || null;
		  if (!this.selectedPrefillCase || this.selectedPrefillCase.status === 'completed') { this.accessStatus = 'denied'; return; }
		  this.answers = this.selectedPrefillCase.answers || [];
		}
        await this.restoreDraft();
        this.accessStatus = "ready";
      } catch (error) {
        if (error.response && error.response.status === 401 && error.response.data && error.response.data.code === "SESSION_REQUIRED") {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.accessStatus = error.response && error.response.status === 404 ? "denied" : "error";
      }
    },
    async restoreDraft() {
      try {
		const response = await this.terrainApi.getDraft(this.campaignId, this.prefillCaseId);
        const draft = response && response.data;
        if (!draft || draft.templateVersionId !== this.content.templateVersionId) return;
        this.answers = draft.answers || [];
        this.gpsOriginal = draft.gpsOriginal || null;
        this.gpsFinal = draft.gpsFinal || null;
        this.draftClientRevision = draft.clientRevision || 0;
        this.draftResourceVersion = draft.resourceVersion || 0;
        this.$store.commit('terrain/SET_AUTOSAVE_STATE', { status: 'saved', draft });
      } catch (error) {
        // A 404 means that this respondent has not started a draft yet. Other
        // failures do not block the authorized form; the controller explains
        // any later save failure while retaining the in-memory values.
        if (!error.response || error.response.status !== 404) this.$store.commit('terrain/SET_AUTOSAVE_STATE', { status: 'error' });
      }
    },
    applySavedDraft(draft) {
      this.draftClientRevision = draft.clientRevision;
      this.draftResourceVersion = draft.resourceVersion;
    },
    onGpsCaptured({ kind, capture }) {
      if (kind === "gpsOriginal") this.gpsOriginal = capture;
      if (kind === "gpsFinal") this.gpsFinal = capture;
    },
    onAnswersChanged(value) {
      this.answers = value;
      this.autosaveReady = true;
    },
    async openSubmitModal() {
      this.submitError = "";
      this.gpsBlockedMessage = "";
      this.$modal.show("terrain-submission-confirm");
      await this.retryFinal();
    },
    closeSubmitModal() {
      this.$modal.hide("terrain-submission-confirm");
    },
    onLocationPicked(coordinates) {
      if (!this.gpsFinal) return;
      this.gpsFinal = { ...this.gpsFinal, corrected: { type: "Point", coordinates, correctedAt: new Date().toISOString() } };
    },
    async retryFinal() {
      this.capturingFinal = true;
      this.gpsBlockedMessage = "";
      try {
        await this.$refs.gpsCapture.captureForSubmission();
      } finally {
        this.capturingFinal = false;
      }
    },
    describeSubmitError(error) {
      const data = error && error.response && error.response.data;
      if (data && data.code === "TERRAIN_VALIDATION_FAILED" && Array.isArray(data.fieldErrors) && data.fieldErrors.length) {
        return data.fieldErrors.map((fieldError) => fieldError.message).join(" ");
      }
      if (data && SUBMIT_ERROR_MESSAGES[data.code]) return SUBMIT_ERROR_MESSAGES[data.code];
      return "No fue posible enviar la respuesta. Intente nuevamente.";
    },
    async confirmSubmit() {
      this.submitting = true;
      this.submitError = "";
      try {
        await this.terrainApi.submitResponse(this.campaignId, {
          answers: this.answers,
          gpsOriginal: this.gpsOriginal,
          gpsFinal: this.gpsFinal,
		  ...(this.prefillCaseId ? { prefillCaseId: this.prefillCaseId } : {}),
        });
        this.$modal.hide("terrain-submission-confirm");
        this.accessStatus = "submitted";
      } catch (error) {
        this.submitError = this.describeSubmitError(error);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-campaign-form {
  background: var(--color-mist);
  box-sizing: border-box;
  min-height: 100vh;
  overflow-x: hidden;
  padding: var(--terrain-space-lg);
  width: 100%;
  margin: 0;
}

.terrain-campaign-form__content {
  display: grid;
  gap: var(--terrain-space-xl);
  margin: 0 auto;
  max-width: 760px;
  min-width: 0;
  width: 100%;
}

.terrain-campaign-form__header,
.terrain-campaign-form__status,
.terrain-campaign-form__notice {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-lg);
}

.terrain-campaign-form__gps-hidden { display: none; }

// position:fixed (not display:none) so the box keeps real dimensions and
// OpenLayers actually computes a resolution/extent to request tiles for.
.terrain-campaign-form__map-preload {
  position: fixed;
  top: 0;
  left: -9999px;
  width: 440px;
  height: 280px;
  pointer-events: none;
}

.terrain-campaign-form__footer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
  justify-content: flex-end;
}

.terrain-campaign-form__submit {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover { background: var(--color-steel); }
}

.terrain-submission-modal {
  display: grid;
  gap: var(--terrain-space-md);
  padding: var(--terrain-space-lg);
}

.terrain-submission-modal h2 { margin: 0; color: var(--color-navy); }
.terrain-submission-modal__hint { margin: 0; color: var(--color-ghost); }
.terrain-submission-modal__status { margin: 0; color: var(--color-ghost); }

.terrain-submission-modal__accuracy {
  align-items: center;
  color: var(--color-graphite);
  display: flex;
  font-weight: 600;
  gap: var(--terrain-space-md);
  justify-content: space-between;
}

.terrain-submission-modal__gps-btn {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-steel);
  border-radius: 50%;
  color: var(--color-navy);
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 2.25rem;
  justify-content: center;
  width: 2.25rem;
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-navy); border-color: var(--color-navy); color: var(--color-white); }
  &:disabled { cursor: wait; opacity: .65; }
}

.terrain-submission-modal__retry {
  align-self: flex-start;
  background: var(--color-white);
  border: 1px solid var(--color-steel);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  padding: var(--terrain-space-xs) var(--terrain-space-md);
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-navy); border-color: var(--color-navy); color: var(--color-white); }
  &:disabled { cursor: wait; opacity: .65; }
}

.terrain-submission-modal__error { color: var(--color-danger); margin: 0; }

.terrain-submission-modal__actions {
  display: flex;
  gap: var(--terrain-space-md);
  justify-content: flex-end;
}

.terrain-submission-modal__cancel,
.terrain-submission-modal__confirm {
  border-radius: var(--terrain-radius-xl);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);
}

.terrain-submission-modal__cancel {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  color: var(--color-navy);

  &:hover:not(:disabled) { background: var(--color-mist); }
}

.terrain-submission-modal__confirm {
  background: var(--color-navy);
  border: 0;
  color: var(--color-white);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}

.terrain-campaign-form__header h1,
.terrain-campaign-form__header p,
.terrain-campaign-form__notice h1,
.terrain-campaign-form__notice p {
  margin: 0;
}

.terrain-campaign-form__header > p:last-child { color: var(--color-ghost); margin-top: var(--terrain-space-sm); }
.terrain-campaign-form__notice p { margin-top: var(--terrain-space-sm); color: var(--color-ghost); }

.terrain-campaign-form__eyebrow {
  color: var(--color-orange);
  font-size: .875rem;
  font-weight: 700;
  margin-bottom: var(--terrain-space-xs) !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.terrain-campaign-form__notice button {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  margin-top: var(--terrain-space-lg);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  transition: background-color var(--terrain-transition);

  &:hover { background: var(--color-steel); }
}

@media (max-width: 480px) {
  .terrain-campaign-form { padding: var(--terrain-space-md); }
  .terrain-campaign-form__header { padding: var(--terrain-space-md); }
  .terrain-campaign-form__footer { justify-content: stretch; }
  .terrain-campaign-form__submit { width: 100%; }
}
</style>
